import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore'; // Import setDoc

const SettingsForm = () => {
    const [homepageContent, setHomepageContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({});

    // Define default content for the homepage document
    const defaultHomepageContent = {
        hero: {
            title: "LOS SANTOS",
            subtitle: "FIRE DEPARTMENT",
            description: "\"We are here to protect lives and property. With courage and determination, we serve the community of Los Santos.\\\"\"",
            discordUrl: "",
            discordButtonText: "Discord",
            // Default text colors for hero section
            titleColor: "#111827",
            subtitleColor: "#111827",
            descriptionColor: "#111827",
            discordButtonTextColor: "#111827"
        },
        aboutUs: {
            text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
            // Default text color for About Us main text
            textColor: "#111827",
            cards: [
                {
                    icon: "💰", // Or URL to icon
                    title: "High Financial Rewards",
                    description: "Lorem ipsum dolor sit amet, consectetur.",
                    // Default colors for card texts
                    titleColor: "#111827",
                    descriptionColor: "#111827"
                },
                {
                    icon: "🔥",
                    title: "Strong Experience and Adrenaline",
                    description: "Lorem ipsum dolor sit amet, consectetur.",
                    titleColor: "#111827",
                    descriptionColor: "#111827"
                },
                {
                    icon: "Heart",
                    title: "New Opportunities",
                    description: "Discover exciting career paths and personal growth.",
                    titleColor: "#111827",
                    descriptionColor: "#111827"
                },]
        }
    };

    useEffect(() => {
        const fetchOrCreateSettings = async () => {
            try {
                const homepageDocRef = doc(db, 'content', 'homepage');
                const homepageDocSnap = await getDoc(homepageDocRef);

                if (homepageDocSnap.exists()) {
                    const fetchedData = homepageDocSnap.data();
                    setHomepageContent(fetchedData);
                    setFormData(fetchedData);
                } else {
                    console.log('Homepage content document not found. Creating with default content.');
                    await setDoc(homepageDocRef, defaultHomepageContent);
                    setHomepageContent(defaultHomepageContent);
                    const newHomepageDocSnap = await getDoc(homepageDocRef);
                    if (newHomepageDocSnap.exists()) {
                        setHomepageContent(newHomepageDocSnap.data());
                        setFormData(newHomepageDocSnap.data());
                    } else {
                        setError('Failed to create and fetch homepage content.');
                    }
                }
            } catch (err) {
                console.error('Error fetching homepage content:', err);
                setError('Failed to fetch homepage content.');
            } finally {
                setLoading(false);
            }
        };

        fetchOrCreateSettings();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const keys = name.split('.');
        if (keys.length > 1) {
            setFormData(prevState => {
                const newState = { ...prevState };
                let current = newState;
                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]]) current[keys[i]] = {};
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = value;
                return newState;
            });
        } else {
            setFormData(prevState => ({
                ...prevState,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const homepageDocRef = doc(db, 'content', 'homepage');
            await updateDoc(homepageDocRef, formData);
            setSuccess(true);
        } catch (err) {
            console.error('Error saving homepage content:', err);
            setError('Failed to save homepage content.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="p-6">Loading text settings...</div>;
    }

    if (error && !homepageContent) {
        return <div className="p-6 text-red-500">Error: {error}</div>;
    }


    return (
        <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-6">Homepage Text Settings</h2>
            {homepageContent && (
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Hero Section */}
                    <div>
                        <h3 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-200">Hero Section</h3>
                        <div className="mb-6">
                            <label htmlFor="hero.title" className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                            <input
                                type="text"
                                id="hero.title"
                                name="hero.title"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                value={formData.hero?.title || ''}
                                onChange={handleInputChange}
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <input
                                    type="color"
                                    id="hero.titleColor"
                                    name="hero.titleColor"
                                    className="color-input-compact"
                                    value={formData.hero?.titleColor || '#111827'}
                                    onChange={handleInputChange}
                                />
                                <div className="w-5 h-5 rounded-sm border" style={{ backgroundColor: formData.hero?.titleColor || '#111827' }}></div>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label htmlFor="hero.subtitle" className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                            <input
                                type="text"
                                id="hero.subtitle"
                                name="hero.subtitle"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                value={formData.hero?.subtitle || ''}
                                onChange={handleInputChange}
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <input
                                    type="color"
                                    id="hero.subtitleColor"
                                    name="hero.subtitleColor"
                                    className="color-input-compact"
                                    value={formData.hero?.subtitleColor || '#111827'}
                                    onChange={handleInputChange}
                                />
                                <div className="w-5 h-5 rounded-sm border" style={{ backgroundColor: formData.hero?.subtitleColor || '#111827' }}></div>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label htmlFor="hero.description" className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <textarea
                                id="hero.description"
                                name="hero.description"
                                rows="3"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                value={formData.hero?.description || ''}
                                onChange={handleInputChange}
                            ></textarea>
                            <div className="flex items-center gap-2 mt-2">
                                <input
                                    type="color"
                                    id="hero.descriptionColor"
                                    name="hero.descriptionColor"
                                    className="color-input-compact"
                                    value={formData.hero?.descriptionColor || '#111827'}
                                    onChange={handleInputChange}
                                />
                                <div className="w-5 h-5 rounded-sm border" style={{ backgroundColor: formData.hero?.descriptionColor || '#111827' }}></div>
                            </div>
                        </div>
                        {/* Discord Invite URL Field */}
                        <div className="mb-4">
                            <label htmlFor="hero.discordUrl" className="block text-sm font-medium text-gray-700 mb-2">Discord Invite URL</label>
                            <input
                                type="text"
                                id="hero.discordUrl"
                                name="hero.discordUrl"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                value={formData.hero?.discordUrl || ''}
                                onChange={handleInputChange}
                            />
                        </div>
                        {/* Discord Button Text Field */}
                        <div className="mb-4">
                            <label htmlFor="hero.discordButtonText" className="block text-sm font-medium text-gray-700 mb-2">Discord Button Text</label>
                            <input
                                type="text"
                                id="hero.discordButtonText"
                                name="hero.discordButtonText"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                value={formData.hero?.discordButtonText || ''}
                                onChange={handleInputChange}
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <input
                                    type="color"
                                    id="hero.discordButtonTextColor"
                                    name="hero.discordButtonTextColor"
                                    className="color-input-compact"
                                    value={formData.hero?.discordButtonTextColor || '#111827'}
                                    onChange={handleInputChange}
                                />
                                <div className="w-5 h-5 rounded-sm border" style={{ backgroundColor: formData.hero?.discordButtonTextColor || '#111827' }}></div>
                            </div>
                        </div>


                    </div>

                    {/* About Us Section */}
                    <div>
                        <h3 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-200">About Us Section</h3>
                        <div className="mb-4">
                            <label htmlFor="aboutUs.text" className="block text-sm font-medium text-gray-700 mb-2">About Us Text</label>
                            <textarea
                                id="aboutUs.text"
                                name="aboutUs.text"
                                rows="5"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                value={formData.aboutUs?.text || ''}
                                onChange={handleInputChange}
                            ></textarea>
                            <div className="flex items-center gap-2 mt-2">
                                <input
                                    type="color"
                                    id="aboutUs.textColor"
                                    name="aboutUs.textColor"
                                    className="color-input-compact"
                                    value={formData.aboutUs?.textColor || '#111827'}
                                    onChange={handleInputChange}
                                />
                                <div className="w-5 h-5 rounded-sm border" style={{ backgroundColor: formData.aboutUs?.textColor || '#111827' }}></div>
                            </div>
                        </div>

                        {[0, 1, 2].map((index) => {

                            const card = formData.aboutUs?.cards?.[index] || {};
                            return (
                                <div key={index} className="border border-gray-200 p-4 rounded-md mb-4 last:mb-0">
                                    <h4 className="text-lg font-medium mb-3 text-gray-800">Card {index + 1}</h4>

                                    <div className="mb-3">
                                        <label htmlFor={`aboutUs.cards[${index}].icon`} className="block text-sm font-medium text-gray-700 mb-2">Icon (Lucide-react icon name, e.g., 'Heart')</label>
                                        <input
                                            type="text"
                                            id={`aboutUs.cards[${index}].icon`}
                                            name={`aboutUs.cards[${index}].icon`}
                                            className="shadow appearance-none border rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                            value={card.icon || ''}
                                            onChange={(e) => {
                                                const newCards = [...(formData.aboutUs?.cards || defaultHomepageContent.aboutUs.cards)]; // Use existing cards or default if none exist

                                                if (!newCards[index]) {
                                                    newCards[index] = { ...defaultHomepageContent.aboutUs.cards[index] || {} }; // Use default card data if available
                                                }
                                                newCards[index].icon = e.target.value;
                                                setFormData(prevState => ({
                                                    ...prevState,
                                                    aboutUs: {
                                                        ...prevState.aboutUs,
                                                        cards: newCards
                                                    }
                                                }));
                                            }}
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label htmlFor={`aboutUs.cards[${index}].title`} className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                                        <input
                                            type="text"
                                            id={`aboutUs.cards[${index}].title`}
                                            name={`aboutUs.cards[${index}].title`}
                                            className="shadow appearance-none border rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                            value={card.title || ''}
                                            onChange={(e) => {
                                                const newCards = [...(formData.aboutUs?.cards || defaultHomepageContent.aboutUs.cards)];
                                                 if (!newCards[index]) {
                                                    newCards[index] = { ...defaultHomepageContent.aboutUs.cards[index] || {} };
                                                }
                                                newCards[index].title = e.target.value;
                                                setFormData(prevState => ({
                                                    ...prevState,
                                                    aboutUs: {
                                                        ...prevState.aboutUs,
                                                        cards: newCards
                                                    }
                                                }));
                                            }}
                                        />
                                        <div className="flex items-center gap-2 mt-2">
                                            <input
                                                type="color"
                                                id={`aboutUs.cards[${index}].titleColor`}
                                                name={`aboutUs.cards[${index}].titleColor`}
                                                className="color-input-compact"
                                                value={card.titleColor || '#111827'}
                                                onChange={(e) => {
                                                    const newCards = [...(formData.aboutUs?.cards || defaultHomepageContent.aboutUs.cards)];
                                                    if (!newCards[index]) {
                                                        newCards[index] = { ...defaultHomepageContent.aboutUs.cards[index] || {} };
                                                    }
                                                    newCards[index].titleColor = e.target.value;
                                                    setFormData(prevState => ({
                                                        ...prevState,
                                                        aboutUs: {
                                                            ...prevState.aboutUs,
                                                            cards: newCards
                                                        }
                                                    }));
                                                }}
                                            />
                                            <div className="w-5 h-5 rounded-sm border" style={{ backgroundColor: card.titleColor || '#111827' }}></div>
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <label htmlFor={`aboutUs.cards[${index}].description`} className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <textarea
                                            id={`aboutUs.cards[${index}].description`}
                                            name={`aboutUs.cards[${index}].description`}
                                            rows="2"
                                            className="shadow appearance-none border rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                            value={card.description || ''}
                                            onChange={(e) => {
                                                const newCards = [...(formData.aboutUs?.cards || defaultHomepageContent.aboutUs.cards)];
                                                 if (!newCards[index]) {
                                                    newCards[index] = { ...defaultHomepageContent.aboutUs.cards[index] || {} };
                                                }
                                                newCards[index].description = e.target.value;
                                                setFormData(prevState => ({
                                                    ...prevState,
                                                    aboutUs: {
                                                        ...prevState.aboutUs,
                                                        cards: newCards
                                                    }
                                                }));
                                            }}
                                        ></textarea>
                                        <div className="flex items-center gap-2 mt-2">
                                            <input
                                                type="color"
                                                id={`aboutUs.cards[${index}].descriptionColor`}
                                                name={`aboutUs.cards[${index}].descriptionColor`}
                                                className="color-input-compact"
                                                value={card.descriptionColor || '#111827'}
                                                onChange={(e) => {
                                                    const newCards = [...(formData.aboutUs?.cards || defaultHomepageContent.aboutUs.cards)];
                                                    if (!newCards[index]) {
                                                        newCards[index] = { ...defaultHomepageContent.aboutUs.cards[index] || {} };
                                                    }
                                                    newCards[index].descriptionColor = e.target.value;
                                                    setFormData(prevState => ({
                                                        ...prevState,
                                                        aboutUs: {
                                                            ...prevState.aboutUs,
                                                            cards: newCards
                                                        }
                                                    }));
                                                }}
                                            />
                                            <div className="w-5 h-5 rounded-sm border" style={{ backgroundColor: card.descriptionColor || '#111827' }}></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>


                    <div className="flex items-center justify-between mt-6">
                        <button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 transition duration-300"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                        {success && <p className="text-green-500 text-sm">Changes saved successfully!</p>}
                        {error && <p className="text-red-500 text-sm">Error: {error}</p>}
                    </div>
                </form>
            )}
        </div>
    );
};

export default SettingsForm;